import { Suspense, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Redirect, Switch, useRouteMatch } from 'react-router-dom';
import { Loader } from '@givebutter/primitives/loader';
import Content from 'assets/js/components/Content';
import { AuctionEndedNotification } from 'assets/js/pages/campaign-settings/AuctionEndedNotification';
import { AuctionEndingNotification } from 'assets/js/pages/campaign-settings/AuctionEndingNotification';
import { CampaignHeader } from 'assets/js/pages/campaign-settings/CampaignHeader';
import CampaignDetails from 'assets/js/pages/campaign-settings/campaign-details/CampaignDetails';
import { CampaignEventRouter } from 'assets/js/pages/campaign-settings/campaign-event/CampaignEventRouter';
import CampaignSettingsTab from 'assets/js/pages/campaign-settings/campaign-settings/CampaignSettings';
import { CampaignTeamRouter } from 'assets/js/pages/campaign-settings/campaign-team/CampaignTeamRouter';
import CampaignTools from 'assets/js/pages/campaign-settings/campaign-tools/CampaignTools';
import { handleRateLimitError } from 'components/error-boundaries/RateLimitErrorBoundary';
import * as NavMenu from 'components/nav-menu';
import { AuctionRouter } from 'components/pages/campaign-settings/campaign-auction/auction-router';
import { CampaignSharingRouter } from 'components/pages/campaign-settings/campaign-sharing/sharing-router';
import { Route } from 'components/router/Route';
import { RenderOrRedirect } from 'components/util';
import { IntercomProductTourTargets } from 'constants/intercom';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useDefaultTimezone } from 'customHooks/useDefaultTimezone';
import { Campaign } from 'types/domain';

const CampaignSettingsRoutes = {
    Team: 'members',
    Event: 'event',
    Sharing: 'sharing',
    Tools: 'tools',
    Auction: 'auction',
    Settings: 'settings',
};

const permittedTypesFactory = (campaign: Campaign) => (campaignTypes: string[]) => {
    return campaignTypes.some(type => campaign.type === type);
};

const CampaignRouter = () => {
    const match = useRouteMatch();
    const { data: campaign } = useCurrentCampaign();
    const { didInit } = useDefaultTimezone(campaign.timezone);
    const permissibleTabs = useMemo(() => permittedTypesFactory(campaign), [campaign]);

    if (!didInit) {
        return null;
    }

    return (
        <div key={campaign.id} className={'flex flex-col gap-4'}>
            <AuctionEndedNotification />
            <AuctionEndingNotification />
            <CampaignHeader />
            <NavMenu.Root aria-label="Campaign settings navigation">
                <NavMenu.Item exact={true} to={`${match.url}`}>
                    Details
                </NavMenu.Item>
                {permissibleTabs(['event', 'fundraise']) && (
                    <NavMenu.Item to={`${match.url}/${CampaignSettingsRoutes.Team}`}>
                        Peer-to-Peer
                    </NavMenu.Item>
                )}
                {permissibleTabs(['event', 'fundraise']) && (
                    <NavMenu.Item to={`${match.url}/${CampaignSettingsRoutes.Event}`}>
                        Event
                    </NavMenu.Item>
                )}
                <NavMenu.Item to={`${match.url}/${CampaignSettingsRoutes.Sharing}`}>
                    Sharing
                </NavMenu.Item>
                <NavMenu.Item to={`${match.url}/${CampaignSettingsRoutes.Tools}`}>
                    Tools
                </NavMenu.Item>
                {permissibleTabs(['event', 'fundraise']) && (
                    <NavMenu.Item
                        data-intercom-target={IntercomProductTourTargets.CampaignAuctionsTab}
                        to={`${match.url}/${CampaignSettingsRoutes.Auction}`}
                    >
                        Auction
                    </NavMenu.Item>
                )}
                <NavMenu.Item to={`${match.url}/${CampaignSettingsRoutes.Settings}`}>
                    Settings
                </NavMenu.Item>
            </NavMenu.Root>
            <Suspense fallback={<Loader />}>
                <Switch>
                    <Route exact={true} path={match.path} component={CampaignDetails} />
                    <Route path={`${match.path}/${CampaignSettingsRoutes.Team}`}>
                        <RenderOrRedirect
                            cond={permissibleTabs(['event', 'fundraise'])}
                            redirectTo={match.url}
                        >
                            <CampaignTeamRouter />
                        </RenderOrRedirect>
                    </Route>
                    <Route path={`${match.path}/${CampaignSettingsRoutes.Event}`}>
                        <RenderOrRedirect
                            cond={permissibleTabs(['event', 'fundraise'])}
                            redirectTo={match.url}
                        >
                            <CampaignEventRouter />
                        </RenderOrRedirect>
                    </Route>
                    <Route path={`${match.path}/${CampaignSettingsRoutes.Sharing}`}>
                        <CampaignSharingRouter />
                    </Route>
                    <Route
                        path={`${match.path}/${CampaignSettingsRoutes.Tools}`}
                        component={CampaignTools}
                    />
                    <Route path={`${match.path}/${CampaignSettingsRoutes.Auction}`}>
                        <RenderOrRedirect
                            cond={permissibleTabs(['event', 'fundraise'])}
                            redirectTo={match.url}
                        >
                            <AuctionRouter />
                        </RenderOrRedirect>
                    </Route>
                    <Route
                        path={`${match.path}/${CampaignSettingsRoutes.Settings}`}
                        component={CampaignSettingsTab}
                    />
                    <Route>
                        <Redirect to={match.url} />
                    </Route>
                </Switch>
            </Suspense>
        </div>
    );
};

const CampaignSettings = () => {
    const { accountId } = useCurrentAccount();
    return (
        <ErrorBoundary
            onError={handleRateLimitError}
            fallback={<Redirect to={`/accounts/${accountId}/campaigns`} />}
        >
            <Content>
                <CampaignRouter />
            </Content>
        </ErrorBoundary>
    );
};

export default CampaignSettings;
